import { RawOrderbookEntry } from '../model/types';

/**
 * Calculate number of decimal places in a tick size
 * Used to format the final rounded price correctly
 *
 * Uses logarithmic approach to handle scientific notation (1e-8) correctly
 * instead of relying on toString() which fails for very small numbers
 *
 * @example
 * getDecimalPlaces(0.01) → 2
 * getDecimalPlaces(0.1) → 1
 * getDecimalPlaces(1) → 0
 * getDecimalPlaces(10) → 0
 * getDecimalPlaces(0.00000001) → 8  (handles 1e-8 correctly)
 * getDecimalPlaces(0.000001) → 6    (handles 1e-6 correctly)
 */
function getDecimalPlaces(tickSize: number): number {
  if (tickSize >= 1) return 0;

  // Use logarithm to determine decimal places
  // This handles scientific notation (1e-8, 1e-6) correctly
  // log10(0.01) = -2 → 2 decimal places
  // log10(0.00000001) = -8 → 8 decimal places
  return Math.max(0, -Math.floor(Math.log10(tickSize)));
}

/**
 * Rounds a price to the nearest tick size for matching with aggregated levels
 * Uses ask/bid rounding rules to avoid crossing the book.
 */
export function roundPriceToTick(
  price: string | number,
  tickSize: number | null,
  side: 'ask' | 'bid',
): string {
  if (tickSize === null || tickSize <= 0) return String(price);

  const priceNum = typeof price === 'number' ? price : Number(price);
  if (!Number.isFinite(priceNum)) return String(price);

  const roundFn = side === 'ask' ? Math.ceil : Math.floor;
  const decimalPlaces = getDecimalPlaces(tickSize);

  return (roundFn(priceNum / tickSize) * tickSize).toFixed(decimalPlaces);
}

/**
 * Aggregates orderbook entries by tick size (price grouping)
 * This is how real exchanges (Binance, OKX, Bybit) work
 *
 * @param entries - Raw orderbook entries
 * @param tickSize - Minimum price increment (e.g., 0.01, 0.1, 1, 10, 100)
 * @param side - 'ask' or 'bid' for proper rounding direction
 *
 * @example
 * // With tickSize=10 for BTC/USDT at ~50,000:
 * Input: [50123.45, 50125.67, 50128.12]
 * Output: [50120, 50120, 50120] (all grouped to 50120)
 *
 * @example
 * // With tickSize=0.1 for ETH/USDT at ~3,000:
 * Input: [3000.12, 3000.18, 3000.25]
 * Output: [3000.1, 3000.1, 3000.2] (grouped by 0.1 increments)
 */
export function aggregateOrdersByTickSize(
  entries: RawOrderbookEntry[],
  tickSize: number | null,
  side: 'ask' | 'bid',
): RawOrderbookEntry[] {
  // If no tick size or empty entries, return as-is
  if (tickSize === null || tickSize <= 0 || entries.length === 0) {
    return entries;
  }

  const aggregated = new Map<string, number>();
  const roundFn = side === 'ask' ? Math.ceil : Math.floor;
  const decimalPlaces = getDecimalPlaces(tickSize);

  for (const entry of entries) {
    const priceNum = Number(entry.price);
    const amountNum = Number(entry.amount);

    if (isNaN(priceNum) || isNaN(amountNum)) continue;

    // Round price to nearest tick size multiple
    // For asks: round UP (ceil) - never quote below actual price
    // For bids: round DOWN (floor) - never quote above actual price
    const roundedPrice = (roundFn(priceNum / tickSize) * tickSize).toFixed(decimalPlaces);

    const existingAmount = aggregated.get(roundedPrice) || 0;
    aggregated.set(roundedPrice, existingAmount + amountNum);
  }

  // Convert map back to array and sort
  const result: RawOrderbookEntry[] = Array.from(aggregated.entries()).map(([price, amount]) => ({
    price,
    amount: amount.toString(),
  }));

  // Sort once per diff packet: asks ascending (lowest first), bids descending (highest first)
  result.sort((a, b) => {
    const priceA = Number(a.price);
    const priceB = Number(b.price);

    if (!Number.isFinite(priceA) || !Number.isFinite(priceB)) {
      return 0;
    }

    return side === 'ask' ? priceA - priceB : priceB - priceA;
  });

  return result;
}

// Keep old function for backward compatibility (deprecated)
/** @deprecated Use aggregateOrdersByTickSize instead */
export function aggregateOrdersByPrecision(
  entries: RawOrderbookEntry[],
  precision: number | null,
  side: 'ask' | 'bid',
): RawOrderbookEntry[] {
  // Convert precision to tick size and use new function
  if (precision === null) return entries;
  const tickSize = 1 / Math.pow(10, precision);
  return aggregateOrdersByTickSize(entries, tickSize, side);
}

/**
 * Formats a price according to precision
 */
export function formatPrice(price: string | number, precision: number | null): string {
  if (precision === null) return String(price);
  return Number(price).toFixed(precision);
}

/**
 * Generates available precision levels from exchange pair precision
 * Following the pattern from existing Tick component
 */
export function getPrecisionLevels(pairPrecision: number): number[] {
  const levels: number[] = [];

  // Start from pair precision and go up in steps
  for (let i = 0; i <= 5; i++) {
    const level = pairPrecision - i;
    if (level >= 0) {
      levels.push(level);
    }
  }

  return levels.reverse(); // Most precise first
}
